import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowUpRight,
  ClipboardCheck,
  Palette,
  Ruler,
  Truck,
} from 'lucide-react';
import {
  homeScrollItem,
  homeScrollStagger,
  homeScrollViewport,
} from '../../utils/homeScrollMotion';
import styles from './HomeAboutSection.module.css';

const highlights = [
  {
    icon: Palette,
    label: 'Print & branding',
    detail:
      'Cup sleeves and wraps with clean registration — built for franchise colours and campaign artwork.',
    accent: 'warm',
  },
  {
    icon: Ruler,
    label: 'Specs your way',
    detail:
      'GSM, width, coating, and roll size discussed upfront so production matches your die and line setup.',
    accent: 'green',
  },
  {
    icon: Truck,
    label: 'Planned dispatch',
    detail:
      'Repeat schedules for distributors and plants — fewer last-minute gaps when volumes spike.',
    accent: 'warm',
  },
  {
    icon: ClipboardCheck,
    label: 'Batch-wise QC',
    detail:
      'Checks on finish, reel tightness, and food-contact grades before material leaves the floor.',
    accent: 'green',
  },
];

const HomeAboutSection = () => (
  <section
    className={`${styles.section} home-bg-sage`}
    aria-labelledby="home-about-title"
  >
    <div className={styles.container}>
      <motion.div
        className={styles.frame}
        variants={homeScrollItem}
        initial="hidden"
        whileInView="visible"
        viewport={homeScrollViewport}
      >
        <span className={styles.cutBar} aria-hidden="true" />
        <span className={styles.cutCorner} aria-hidden="true" />

        <div className={styles.inner}>
          <div className={styles.copy}>
            <span className={styles.kicker}>About us</span>
            <h2 id="home-about-title" className={styles.title}>
              Greeneri Paper Tradelinks
            </h2>
            <p className={styles.textLead}>
              Since <strong>2018</strong>, Greeneri Paper Tradelinks has grown from a Meerut-based mill
              into a long-term manufacturing and supply partner for brands that depend on paper packaging
              every day — not one-off spot purchases.
            </p>
            <p className={styles.text}>
              Our range covers cup sleeves, ripple sleeves, food wrap, paper plates, PE coated rolls,
              bobbins, and custom roll stock — produced for QSR chains, cafés, converters, distributors,
              and industrial packers across India.
            </p>
            <p className={styles.text}>
              Whether you need a new print program, a stable GSM for a converting line, or recurring bulk
              dispatch, our team aligns specs, timelines, and quality checks with how your operations
              actually run — so repeat orders feel predictable, not stressful.
            </p>

            <Link to="/about" className={styles.link}>
              Learn more about us
              <ArrowUpRight size={15} strokeWidth={2.5} aria-hidden="true" />
            </Link>
          </div>

          <div className={styles.highlightsWrap}>
            <span className={styles.highlightsGlow} aria-hidden="true" />
            <span className={styles.highlightsTrack} aria-hidden="true" />
            <p className={styles.highlightsHead}>How we work with you</p>

            <motion.ul
              className={styles.highlights}
              variants={homeScrollStagger}
              initial="hidden"
              whileInView="visible"
              viewport={homeScrollViewport}
            >
              {highlights.map((item, index) => {
                const Icon = item.icon;
                return (
                  <motion.li
                    key={item.label}
                    className={styles.highlight}
                    data-cut={String((index % 3) + 1)}
                    data-accent={item.accent}
                    data-shift={index % 2 === 0 ? 'left' : 'right'}
                    variants={homeScrollItem}
                    whileHover={{ x: index % 2 === 0 ? 6 : -6, transition: { duration: 0.2 } }}
                  >
                    <span className={styles.highlightIndex} aria-hidden="true">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <span className={styles.highlightStripe} aria-hidden="true" />
                    <span className={styles.highlightIcon}>
                      <Icon size={17} strokeWidth={2.25} aria-hidden="true" />
                    </span>
                    <div className={styles.highlightCopy}>
                      <strong>{item.label}</strong>
                      <span>{item.detail}</span>
                    </div>
                  </motion.li>
                );
              })}
            </motion.ul>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

export default HomeAboutSection;
